/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Box,
  Image,
  Grid,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  SliderMark,
} from '@chakra-ui/react';
import Select from 'react-select';
import { useGetAllSpecialitiesQuery, Order_By } from 'src/utils/__generated__/graphql';
import { RestaurantMap } from './RestaurantMap';
import { convertSnakeCaseToWords } from 'src/utils';

const timeOptions = Array.from({ length: 24 * 4 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  return {
    value: `${time}:00`,
    label: time,
  };
});

export interface Field {
  label: string;
  value: any;
}

interface EditDetailsProps {
  formValues: Field[];
  handleInputChange: (label: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  logoUrl: string | undefined;
  coverUrl?: string | undefined;
  handleFileChange: (event: any, type: string) => void;
}

interface LabelMap {
  [key: string]: string;
  specialtyId: string;
  free_distance: string;
  max_distance: string;
  delivery_commission: string;
  admin_commission: string;
  delivery_charge: string;
}

export const labelMap: LabelMap = {
  specialtyId: 'Specialty',
  free_distance: 'Free Distance (Meter)',
  max_distance: 'Max Distance (Meter)',
  delivery_commission: 'Delivery Commission (%)',
  admin_commission: 'Admin Commission (%)',
  delivery_charge: 'Delivery Charge (Rs.)',
  threshold_amount: 'Threshold Amount (Rs.)',
  min_order_amount: 'Min Order Amount (Rs.)',
  isActive: 'Active ?',
  isOnline: 'Online ?',
  opening_hour: 'Opening Hour',
  closing_hour: 'Closing Hour',
};

// First, ensure the field value is always boolean
const getFieldValue = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return false; // default value
};

export const EditDetails: React.FC<EditDetailsProps> = ({
  formValues,
  handleInputChange,
  logoUrl,
  coverUrl,
  handleFileChange,
}) => {
  const { data: specialityData } = useGetAllSpecialitiesQuery({
    variables: {
      order_by: [{ name: Order_By.Asc }],
    },
  });

  const specialtyOptions = useMemo(
    () =>
      specialityData?.specialty?.map((s) => ({
        value: s.id,
        label: s.name,
      })) || [],
    [specialityData]
  );

  const weeklyOffDaysField = formValues.find((field) => field.label === 'weekly_off_days');

  return (
    <Box>
      {/* Images Section */}
      <Grid templateColumns={{ base: '1fr', md: '1fr 2fr' }} gap={6} mb={8}>
        {/* Logo Section */}
        <Box>
          <FormLabel fontSize="lg" mb={3}>
            Logo
          </FormLabel>
          <Box borderRadius="xl" overflow="hidden" boxShadow="md" bg="gray.50" _dark={{ bg: 'gray.700' }}>
            <Image
              objectFit="cover"
              w="100%"
              aspectRatio={1}
              src={logoUrl || '/assets/img/logo-placeholder.jpg'}
              alt="restaurant logo"
            />
            <Box p={4}>
              <input type="file" onChange={(e) => handleFileChange(e, 'logo')} style={{ width: '100%' }} />
            </Box>
          </Box>
        </Box>

        {/* Cover Section */}
        <Box>
          <FormLabel fontSize="lg" mb={3}>
            Cover Image
          </FormLabel>
          <Box borderRadius="xl" overflow="hidden" boxShadow="md" bg="gray.50" _dark={{ bg: 'gray.700' }}>
            <Image
              objectFit="cover"
              w="100%"
              aspectRatio={16 / 9}
              src={coverUrl || '/assets/img/cover-placeholder.jpg'}
              alt="restaurant cover"
            />
            <Box p={4}>
              <input type="file" onChange={(e) => handleFileChange(e, 'cover')} style={{ width: '100%' }} />
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Form Fields */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
        {formValues
          .filter((field) => !['location', 'free_distance', 'max_distance'].includes(field.label))
          ?.map((field) => (
            <FormControl key={field.label} bg="white" _dark={{ bg: 'gray.800' }} p={4} borderRadius="xl" boxShadow="sm">
              <FormLabel fontWeight="medium">{labelMap[field.label] || convertSnakeCaseToWords(field.label)}</FormLabel>
              {
                // field.label === 'short_description' || field.label === 'description' ? (
                //   <Textarea name={field.label} value={field.value as string} onChange={handleInputChange(field.label)} />
                // ) :

                <>
                  {[
                    // 'phone',
                    'delivery_charge',
                    'delivery_commission',
                    'admin_commission',
                    'threshold_amount',
                    'min_order_amount',
                  ].includes(field.label) ? (
                    <NumberInput
                      size="lg"
                      variant="filled"
                      name={field.label}
                      value={field.value ?? ''}
                      onChange={(valueString, valueNumber) => {
                        const syntheticEvent: any = {
                          target: { value: valueNumber },
                        };
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  ) : field.label === 'specialtyId' ? (
                    <Select
                      options={specialtyOptions}
                      value={specialtyOptions.find((option) => option.value === field?.value)}
                      onChange={(selectedOption) => {
                        const syntheticEvent = {
                          target: { value: selectedOption?.value || '' },
                        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                      placeholder="Search Specialty"
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          padding: '4px',
                          borderRadius: '8px',
                        }),
                      }}
                    />
                  ) : field.label === 'type' ? (
                    <Select
                      options={[
                        { value: 'VEG', label: 'Vegetarian' },
                        { value: 'NONVEG', label: 'Non-Vegetarian' },
                        { value: 'MIX', label: 'Mixed' },
                      ]}
                      value={{ value: field?.value, label: field?.value || 'Select Type' }}
                      onChange={(selectedOption) => {
                        const syntheticEvent = {
                          target: {
                            value: selectedOption?.value || '',
                          },
                        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                      placeholder="Select Type"
                      isSearchable
                    />
                  ) : field.label === 'isActive' ? (
                    <Switch
                      name={field.label}
                      isChecked={getFieldValue(field.value)}
                      colorScheme="brand"
                      size="lg"
                      onChange={(e) => {
                        const syntheticEvent = {
                          target: { value: e.target.checked },
                        } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                    />
                  ) : field.label === 'weekly_off_days' ? (
                    <Select
                      options={weekDaysOptions}
                      isMulti
                      value={weekDaysOptions.filter((day) => weeklyOffDaysField?.value?.includes(day.value))}
                      onChange={(selectedOptions) => {
                        const selectedDays = selectedOptions.map((option) => option.value);
                        const syntheticEvent = {
                          target: {
                            value: selectedDays,
                          },
                        } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                      placeholder="Select Weekly Off Days"
                    />
                  ) : field.label === 'opening_hour' || field.label === 'closing_hour' ? (
                    <Select
                      options={timeOptions}
                      value={timeOptions.find((option) => option.value === field.value) || null}
                      onChange={(selectedOption) => {
                        const syntheticEvent = {
                          target: {
                            value: selectedOption?.value || '',
                          },
                        } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                        handleInputChange(field.label)(syntheticEvent);
                      }}
                      placeholder={`Select ${field.label === 'opening_hour' ? 'Opening' : 'Closing'} Time`}
                      isSearchable
                    />
                  ) : (
                    <Input
                      size="lg"
                      variant="filled"
                      type="text"
                      name={field.label}
                      value={field.value !== null ? field.value : ''}
                      onChange={handleInputChange(field.label)}
                    />
                  )}
                </>
              }
            </FormControl>
          ))}
        <Input type="text" name="nothing" disabled />
      </Grid>

      {/* Location Section */}
      <Box bg="white" _dark={{ bg: 'gray.800' }} p={6} borderRadius="xl" boxShadow="sm" mb={8}>
        {formValues
          .filter((field) => field.label === 'location')
          ?.map((field) => (
            <FormControl key={field.label}>
              <FormLabel fontSize="lg" mb={3}>
                Location
              </FormLabel>
              <RestaurantMap
                locationField={field}
                handleInputChange={handleInputChange}
                maxDistance={formValues.find((field) => field.label === 'max_distance')?.value || 0}
                freeDistance={formValues.find((field) => field.label === 'free_distance')?.value || 0}
              />
            </FormControl>
          ))}
      </Box>

      {/* Distance Sliders */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
        <Box bg="white" _dark={{ bg: 'gray.800' }} p={6} borderRadius="xl" boxShadow="sm">
          <FormControl key="free_distance">
            <FormLabel fontSize="lg" mb={3}>
              Free Distance
            </FormLabel>
            <Slider
              value={formValues.find((field) => field.label === 'free_distance')?.value || 0}
              min={0}
              max={1000}
              colorScheme="brand"
              onChange={(value) => {
                const syntheticEvent = {
                  target: { value },
                } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                handleInputChange('free_distance')(syntheticEvent);
              }}
              focusThumbOnChange={false}
            >
              <SliderMark value={0}>0m</SliderMark>
              <SliderMark value={250}>250m</SliderMark>
              <SliderMark value={500}>500m</SliderMark>
              <SliderMark value={1000}>1km</SliderMark>
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb boxSize={6} />
            </Slider>
            <Text fontSize="sm" mt={2} color="gray.600" _dark={{ color: 'gray.400' }}>
              Current value: {formValues.find((field) => field.label === 'free_distance')?.value || 0} meters
            </Text>
          </FormControl>
        </Box>

        <Box bg="white" _dark={{ bg: 'gray.800' }} p={6} borderRadius="xl" boxShadow="sm">
          <FormControl key="max_distance">
            <FormLabel fontSize="lg" mb={3}>
              Max Distance
            </FormLabel>
            <Slider
              value={formValues.find((field) => field.label === 'max_distance')?.value || 0}
              min={formValues.find((field) => field.label === 'free_distance')?.value || 0}
              max={12000}
              colorScheme="brand"
              onChange={(value) => {
                const syntheticEvent = {
                  target: { value },
                } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                handleInputChange('max_distance')(syntheticEvent);
              }}
              focusThumbOnChange={false}
            >
              <SliderMark value={formValues.find((field) => field.label === 'free_distance')?.value || 0}>
                {formValues.find((field) => field.label === 'free_distance')?.value || 0}m
              </SliderMark>
              <SliderMark value={2750}>2.75km</SliderMark>
              <SliderMark value={5000}>5km</SliderMark>
              <SliderMark value={7500}>7.5km</SliderMark>
              <SliderMark value={10000}>10km</SliderMark>
              <SliderMark value={12000}>12km</SliderMark>
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb boxSize={6} />
            </Slider>
            <Text fontSize="sm" mt={2} color="gray.600" _dark={{ color: 'gray.400' }}>
              Current value: {formValues.find((field) => field.label === 'max_distance')?.value || 0} meters
            </Text>
          </FormControl>
        </Box>
      </Grid>
    </Box>
  );
};

const weekDaysOptions = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];
